import type { ColumnDef } from '@tanstack/react-table';
import { AreaRouteCell } from '@/components/common/AreaRouteCell';
import {
  createSerialColumn,
  StackedCellText,
  WrappingCellText,
} from '@/components/common/DataTable';
import { EmptyValue } from '@/components/common/EmptyValue';
import { ExpandableComment } from '@/components/common/ExpandableComment';
import { InstituteNumbers } from '@/components/common/InstituteNumbers';
import { MeetPeopleCell } from '@/components/common/MeetPeopleCell';
import { WorkTypeBadge } from '@/components/common/WorkTypeBadge';
import { formatDisplayDate, formatTime } from '@/utils/date';
import type { WorkSummaryDcrRow, WorkSummaryExpenseRow } from '../types/dashboard.types';
import { ClaimedApprovedAmount } from './ClaimedApprovedAmount';
import { WorkStatusBadge } from './WorkStatusBadge';

const LOCATION_SEPARATOR = ', ';

function joinPresent(parts: (string | null)[], separator: string): string {
  return parts.filter(Boolean).join(separator);
}

export function getWorkSummaryDcrColumns(
  userId: number,
  personName: string | null,
): ColumnDef<WorkSummaryDcrRow, unknown>[] {
  return [
    createSerialColumn<WorkSummaryDcrRow>(),
    {
      id: 'date',
      header: 'Date / Time',
      cell: ({ row }) => (
        <StackedCellText
          primary={formatDisplayDate(row.original.dcrDate)}
          secondary={`${formatTime(row.original.timeFrom)} – ${formatTime(row.original.timeTo)}`}
        />
      ),
    },
    {
      id: 'institution',
      header: 'Institution',
      cell: ({ row }) => (
        <WrappingCellText>
          <StackedCellText
            primary={row.original.instituteName ?? <EmptyValue />}
            secondary={<InstituteNumbers code={row.original.instituteCode} eiin={null} />}
            tertiary={joinPresent(
              [row.original.thanaName, row.original.districtName, row.original.divisionName],
              LOCATION_SEPARATOR,
            )}
          />
        </WrappingCellText>
      ),
    },
    {
      id: 'meetPerson',
      header: 'Meet Person',
      cell: ({ row }) => <MeetPeopleCell meetPeople={row.original.meetPeople} />,
    },
    {
      id: 'area',
      header: 'Area / Comments',
      cell: ({ row }) => (
        <WrappingCellText>
          <AreaRouteCell
            fromName={row.original.areaFrom}
            toName={row.original.areaTo}
            comments={row.original.comments}
            userId={userId}
            personName={personName}
            dcrDate={row.original.dcrDate}
          />
        </WrappingCellText>
      ),
    },
    {
      id: 'workType',
      header: 'Work Type',
      cell: ({ row }) => (
        <WorkTypeBadge code={row.original.workTypeCode} name={row.original.workType} />
      ),
    },
    {
      id: 'transport',
      header: 'Transport',
      cell: ({ row }) => row.original.transportType ?? <EmptyValue />,
    },
    {
      id: 'fare',
      header: 'Fare (Tk)',
      cell: ({ row }) => (
        <ClaimedApprovedAmount
          claimed={row.original.fareAmount}
          approved={row.original.approveFareAmount}
        />
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => <WorkStatusBadge status={row.original.status} />,
    },
  ];
}

export function getWorkSummaryExpenseColumns(): ColumnDef<WorkSummaryExpenseRow, unknown>[] {
  return [
    createSerialColumn<WorkSummaryExpenseRow>(),
    {
      id: 'date',
      header: 'Date',
      cell: ({ row }) => formatDisplayDate(row.original.expenseDate),
    },
    {
      id: 'expenseType',
      header: 'Expense Type',
      cell: ({ row }) => row.original.expenseType ?? <EmptyValue />,
    },
    {
      id: 'cost',
      header: 'Cost (Tk)',
      cell: ({ row }) => (
        <ClaimedApprovedAmount
          claimed={row.original.amount}
          approved={row.original.approveAmount}
        />
      ),
    },
    {
      id: 'vendor',
      header: 'Vendor / Person',
      cell: ({ row }) => (
        <WrappingCellText>{row.original.vendor ?? <EmptyValue />}</WrappingCellText>
      ),
    },
    {
      id: 'comment',
      header: 'Comment',
      cell: ({ row }) =>
        row.original.comments ? (
          <WrappingCellText>
            <ExpandableComment comment={row.original.comments} />
          </WrappingCellText>
        ) : (
          <EmptyValue />
        ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => <WorkStatusBadge status={row.original.status} />,
    },
  ];
}
